import {
  useOrgOverviewByOrganizationQuery,
  useOrgOverviewRebuildMutation,
} from "gql/generated";
import { useMemo, useState } from "react";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import {
  getSortValue,
  OrgOverviewEntityType,
  OrgOverviewTab,
  type OrgOverviewColumnKey,
  type OrgOverviewRow,
  type OrgOverviewSortDirection,
} from "./orgOverviewColumns";

/** Sentinel filter values — kept out of the real data's value space so they can share the
 * same `Select` collection/state as actual status & membership tag names. */
export const FILTER_ALL = "ALL";
export const FILTER_NO_MEMBERSHIP = "NO_MEMBERSHIP";
export const FILTER_NO_STATUS = "NO_STATUS";

export type FilterOption = { value: string; label: string };

type UseOrgOverviewModalProps = {
  organizationId: string;
  isOpen: boolean;
};

export const useOrgOverviewModal = ({
  organizationId,
  isOpen,
}: UseOrgOverviewModalProps) => {
  const { t } = useTranslation("core", { keyPrefix: "ORG_OVERVIEW" });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const [activeTab, setActiveTab] = useState<OrgOverviewTab>(
    OrgOverviewTab.Listings,
  );
  const [searchValue, setSearchValue] = useState("");
  const [sortColumn, setSortColumn] = useState<OrgOverviewColumnKey>("name");
  const [sortDirection, setSortDirection] =
    useState<OrgOverviewSortDirection>("asc");
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [membershipTierFilter, setMembershipTierFilter] =
    useState(FILTER_ALL);
  const [membershipStatusFilter, setMembershipStatusFilter] =
    useState(FILTER_ALL);

  const { data, loading, refetch } = useOrgOverviewByOrganizationQuery({
    variables: { organizationId },
    skip: !isOpen || !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const [rebuildMutation, { loading: isRebuilding }] =
    useOrgOverviewRebuildMutation();

  const allRows = useMemo(
    () => data?.orgOverviewByOrganization?.rows ?? [],
    [data],
  );
  const builtAt = data?.orgOverviewByOrganization?.builtAt ?? null;
  const hasBuiltIndex = allRows.length > 0 || builtAt != null;

  const listingsRows = useMemo(
    () => allRows.filter(row => row.entityType === OrgOverviewEntityType.Listing),
    [allRows],
  );
  const peopleRows = useMemo(
    () => allRows.filter(row => row.entityType === OrgOverviewEntityType.Person),
    [allRows],
  );
  const eventsRows = useMemo(
    () => allRows.filter(row => row.entityType === OrgOverviewEntityType.Event),
    [allRows],
  );

  const isListingsTab = activeTab === OrgOverviewTab.Listings;
  const isPeopleTab = activeTab === OrgOverviewTab.People;
  const isEventsTab = activeTab === OrgOverviewTab.Events;
  const rowsForActiveTab = isListingsTab
    ? listingsRows
    : isPeopleTab
      ? peopleRows
      : eventsRows;

  const normalizedSearch = searchValue.trim().toLowerCase();
  const isSearchActive = normalizedSearch.length > 0;
  const isFilterActive =
    isSearchActive ||
    statusFilter !== FILTER_ALL ||
    membershipTierFilter !== FILTER_ALL ||
    membershipStatusFilter !== FILTER_ALL;

  /** Applies the search box + dropdown filters currently in state to a set of rows.
   * `applyStatus` is false for people rows since the status filter/column only exists for listings. */
  const applySearchAndFilters = (
    rows: OrgOverviewRow[],
    { applyStatus }: { applyStatus: boolean },
  ) => {
    let filtered = isSearchActive
      ? rows.filter(row => row.searchText.includes(normalizedSearch))
      : rows;

    if (applyStatus && statusFilter !== FILTER_ALL) {
      filtered = filtered.filter(row => row.status === statusFilter);
    }
    if (membershipTierFilter !== FILTER_ALL) {
      filtered = filtered.filter(row =>
        membershipTierFilter === FILTER_NO_MEMBERSHIP
          ? !row.membershipTierName
          : row.membershipTierName === membershipTierFilter,
      );
    }
    if (membershipStatusFilter !== FILTER_ALL) {
      filtered = filtered.filter(row =>
        membershipStatusFilter === FILTER_NO_STATUS
          ? !row.membershipStatusNames?.length
          : row.membershipStatusNames?.includes(membershipStatusFilter),
      );
    }

    return filtered;
  };

  const listingsTotal = listingsRows.length;
  const peopleTotal = peopleRows.length;
  const eventsTotal = eventsRows.length;

  const listingsFilteredCount = useMemo(
    () => applySearchAndFilters(listingsRows, { applyStatus: true }).length,
    [
      listingsRows,
      isSearchActive,
      normalizedSearch,
      statusFilter,
      membershipTierFilter,
      membershipStatusFilter,
    ],
  );
  const peopleFilteredCount = useMemo(
    () => applySearchAndFilters(peopleRows, { applyStatus: false }).length,
    [
      peopleRows,
      isSearchActive,
      normalizedSearch,
      membershipTierFilter,
      membershipStatusFilter,
    ],
  );
  const eventsFilteredCount = useMemo(
    () => applySearchAndFilters(eventsRows, { applyStatus: false }).length,
    [eventsRows, isSearchActive, normalizedSearch],
  );

  const statusOptions = useMemo<FilterOption[]>(() => {
    if (!isListingsTab) return [];
    const distinctStatuses = [
      ...new Set(
        rowsForActiveTab
          .map(row => row.status)
          .filter((status): status is string => Boolean(status)),
      ),
    ];
    return [
      { value: FILTER_ALL, label: t("FILTERS.ALL") },
      ...distinctStatuses.map(status => ({
        value: status,
        label: tCore(`PUBLICATION_STATUS.${status}`, { defaultValue: status }),
      })),
    ];
  }, [rowsForActiveTab, isListingsTab, t, tCore]);

  const membershipTierOptions = useMemo<FilterOption[]>(() => {
    if (isEventsTab) return [];
    const distinctTiers = [
      ...new Set(
        rowsForActiveTab
          .map(row => row.membershipTierName)
          .filter((name): name is string => Boolean(name)),
      ),
    ];
    return [
      { value: FILTER_ALL, label: t("FILTERS.ALL") },
      { value: FILTER_NO_MEMBERSHIP, label: t("FILTERS.NO_MEMBERSHIP") },
      ...distinctTiers.map(name => ({ value: name, label: name })),
    ];
  }, [rowsForActiveTab, t, isEventsTab]);

  const membershipStatusOptions = useMemo<FilterOption[]>(() => {
    if (isEventsTab) return [];
    const distinctStatuses = [
      ...new Set(
        rowsForActiveTab.flatMap(row => row.membershipStatusNames ?? []),
      ),
    ];
    return [
      { value: FILTER_ALL, label: t("FILTERS.ALL") },
      { value: FILTER_NO_STATUS, label: t("FILTERS.NO_STATUS") },
      ...distinctStatuses.map(name => ({ value: name, label: name })),
    ];
  }, [rowsForActiveTab, t, isEventsTab]);

  const filteredSortedRows = useMemo(() => {
    const filtered = applySearchAndFilters(rowsForActiveTab, {
      applyStatus: isListingsTab,
    });

    const sorted = [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortColumn);
      const bVal = getSortValue(b, sortColumn);
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [
    rowsForActiveTab,
    isSearchActive,
    normalizedSearch,
    sortColumn,
    sortDirection,
    isListingsTab,
    statusFilter,
    membershipTierFilter,
    membershipStatusFilter,
  ]);

  const handleTabChange = (tab: OrgOverviewTab) => {
    setActiveTab(tab);
    setSortColumn(tab === OrgOverviewTab.Events ? "eventStartDate" : "name");
    setSortDirection(tab === OrgOverviewTab.Events ? "desc" : "asc");
    setStatusFilter(FILTER_ALL);
    setMembershipTierFilter(FILTER_ALL);
    setMembershipStatusFilter(FILTER_ALL);
  };

  const handleSortChange = (column: OrgOverviewColumnKey) => {
    if (column === sortColumn) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleRebuild = async () => {
    try {
      await rebuildMutation({ variables: { organizationId } });
      await refetch();
      toastFactory.create({
        type: "success",
        title: t("REBUILD_SUCCESS"),
      });
    } catch {
      toastFactory.create({
        type: "error",
        title: t("REBUILD_ERROR"),
      });
    }
  };

  return {
    activeTab,
    onTabChange: handleTabChange,
    searchValue,
    onSearchValueChange: setSearchValue,
    sortColumn,
    sortDirection,
    onSortChange: handleSortChange,
    rows: filteredSortedRows,
    totalRowsForTab: rowsForActiveTab.length,
    isSearchActive,
    isFilterActive,
    listingsTotal,
    listingsFilteredCount,
    peopleTotal,
    peopleFilteredCount,
    eventsTotal,
    eventsFilteredCount,
    loading,
    hasBuiltIndex,
    builtAt,
    isRebuilding,
    onRebuild: handleRebuild,
    isListingsTab,
    isPeopleTab,
    isEventsTab,
    statusFilter,
    onStatusFilterChange: setStatusFilter,
    statusOptions,
    membershipTierFilter,
    onMembershipTierFilterChange: setMembershipTierFilter,
    membershipTierOptions,
    membershipStatusFilter,
    onMembershipStatusFilterChange: setMembershipStatusFilter,
    membershipStatusOptions,
  };
};
